import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './index.module.sass';
import Lottie from 'react-lottie';
import Carousel from 'components/carousel';
import Button from 'components/button';
import OnBoardingLayout from 'layout/onboarding';
import { useLoginFlowContext, OTP_TIMES_UP } from '../use-login-flow';
import { clearAuth, clearSkipLogin, skipLogin } from 'service/local-data-provider';
import amplitude from 'service/amplitude';
import appLoader from 'distributor/app-loader';
import lottieIntro from 'lottie/lottie-intro.json';
import lottieGasStation from 'lottie/lottie-gas-station.json';
import lottieParking from 'lottie/lottie-parking.json';
import lottieOnStreetParking from 'lottie/lottie-on-street-parking.json';
import { termsUrl, privacyUrl, payTaipeiCardlessParkingTermsUrl, PATH, isLineBank, isIknow99, isNpcpay } from 'config';
import { useAppContext } from 'use-app';
import { concat, uniq } from 'lodash';
import { isCpcpay } from 'config';

const WelcomeStep = ({ data, isStopped, size }) => {
  return (
    <div className={styles.feature}>
      <Lottie
        isStopped={isStopped}
        width={size}
        height={size}
        options={{
          loop: false,
          animationData: data.lottie,
        }}
      />
      <div className={styles.title}>{data.title}</div>
      <div className={styles.desc}>{data.desc}</div>
    </div>
  );
};

const WelcomePage = () => {
  const history = useHistory();
  const { state: appState } = useAppContext();
  const { dispatch } = useLoginFlowContext();
  const { configData } = appLoader;
  const { welcomeList } = configData;
  const inDesktopBrowser = window.innerWidth >= 768;
  const windowWidth = inDesktopBrowser ? 480 : window.innerWidth;
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [lottiesState, setLottiesState] = useState(() => welcomeList.map(_ => 'stopped'));
  const [supportCitiesCount, setSupportCitiesCount] = useState();

  const introText = () => {
    if (isCpcpay) {
      return '停車場、路邊停車繳費';
    } else if (isNpcpay) {
      return '停車場';
    } else {
      return ' 加油站、停車場、路邊停車繳費';
    }
  };

  const welcomeData = {
    intro: {
      lottie: lottieIntro,
      title: (
        <>
          歡迎使用行車支付
          <br />
          綁定車牌與信用卡
        </>
      ),
      desc: (
        <>
          {introText()}
          <br />
          行車一路自動付款
        </>
      ),
    },
    gasStation: {
      lottie: lottieGasStation,
      title: isIknow99 ? (
        <>
          加油快速付款
          <br />
          同步匯入愛車紀錄
        </>
      ) : (
        <>
          加油免現金
          <br />
          輕鬆一指就付款
        </>
      ),
      desc: isIknow99 ? (
        <>
          一指快速付款，不用掏現金/卡片好輕鬆
          <br />
          自動匯入愛車 App 消費紀錄
        </>
      ) : (
        <>
          進入加油站喊聲麻吉付
          <br />
          手機一指同意付款，不用掏卡好方便
        </>
      ),
    },
    parkinglots: {
      lottie: lottieParking,
      title: (
        <>
          停車繳費免排隊
          <br />
          出場自動扣款
        </>
      ),
      desc: (
        <>
          車牌辨識進出場
          <br />
          出場自動完成扣款
        </>
      ),
    },
    onStreetParking: {
      lottie: lottieOnStreetParking,
      title: (
        <>
          路邊停車自動繳
          <br />
          繳費不怕漏單
        </>
      ),
      desc: (
        <>
          全台 {supportCitiesCount} 縣市路邊停車單自動扣繳
          <br />
          免跑超商，每日自動幫你繳
        </>
      ),
    },
  };

  useEffect(() => {
    let autoPay = appState.bootstraps.autoPayStreetParkingBillsCities.map(x => x.id);
    let scan = appState.bootstraps.scanStreetParkingBillsCities.map(x => x.id);
    let search = appState.bootstraps.searchStreetParkingBillsCities.map(x => x.id);
    let counts = uniq(concat(autoPay, scan, search)).length;
    setSupportCitiesCount(counts);
  }, []); //eslint-disable-line

  useEffect(() => {
    if (!(isLineBank || isIknow99 || isNpcpay)) {
      clearAuth();
    }
    clearSkipLogin();
    amplitude.tutorial_pageview();
    dispatch({ type: OTP_TIMES_UP });
  }, [dispatch]);

  useEffect(() => {
    setLottiesState(prev => {
      let newLottieState = prev.map((state, key) => {
        if (key > carouselIndex) {
          return 'stopped';
        }
        if (state === 'stopped') {
          // 與當前 carouselIndex 相同時變為 play
          return key === carouselIndex ? 'play' : 'stopped';
        }
        return 'play';
      });

      return newLottieState;
    });
  }, [carouselIndex]);

  const Pass = () => {
    return (
      <div
        className={styles.pass}
        onClick={() => {
          amplitude.tutorial_later_onClick();
          skipLogin();
          history.replace(PATH.MAIN);
        }}
      >
        稍後
      </div>
    );
  };

  return (
    <OnBoardingLayout className={styles.welcome}>
      {!(isLineBank || isIknow99) && <Pass />}
      <div className={styles.top}>
        <Carousel
          onSwipe={index => {
            amplitude.tutorial_scroll(index + 1);
            setCarouselIndex(index);
          }}
          loading={false}
          items={welcomeList.map((data, key) => (
            <WelcomeStep
              data={welcomeData[data]}
              isStopped={lottiesState[key] === 'stopped'}
              size={windowWidth === 320 ? 220 : 300}
              key={key}
            />
          ))}
        />
      </div>
      <div className={styles.bottom}>
        <div className={styles.button}>
          <Button
            theme="blue"
            block={true}
            onClick={() => {
              amplitude.tutorial_start_onClick();
              if (isLineBank) {
                history.push(PATH.MAIN);
              } else if (isIknow99) {
                history.push(PATH.USER_ENABLE_ON_STREET_PARKING_AUTO_PAY);
              } else if (isNpcpay) {
                history.push(PATH.USER_REGISTER);
              } else {
                history.push(PATH.USER_LOGIN);
              }
            }}
          >
            立即開始
          </Button>
          {isNpcpay && <div className={styles['npc-hint']}>本服務由麻吉行得通股份有限公司提供</div>}
          {isIknow99 && (
            <div className={styles.aggreement}>
              使用及註冊麻吉付，即代表同意
              <br />
              <a href={termsUrl}>服務條款</a> / <a href={privacyUrl}>隱私權政策</a> /{' '}
              <a href={payTaipeiCardlessParkingTermsUrl}>北市停車條款</a>
            </div>
          )}
        </div>
      </div>
    </OnBoardingLayout>
  );
};

export default WelcomePage;
